import {
  Component,
  DestroyRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import {
  isActive,
  IsActiveMatchOptions,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import { AuthenticationService } from '@core/services';
import {
  CartStore,
  NavigationService,
  ThemeService,
  THEME_TOGGLE_ENABLED,
} from '@shared/services';
import { NttSvg } from '@ui';
import { NttAvatar } from '@ui/avatar';
import { NttButton } from '@ui/button';
import {
  NttDropdownMenu,
  NttDropdownMenuDivider,
  NttDropdownMenuItem,
  NttDropdownMenuItems,
  NttDropdownMenuTrigger,
} from '@ui/dropdown-menu';
import {
  NttNavButton,
  NttNavHamburger,
  NttNavItem,
  NttNavItemIcon,
} from '@ui/navigation';
import { Spinner } from '@ui/spinner';
import { NttBadge } from '@ui/badge';
import { firstValueFrom } from 'rxjs';
import { CartDropdownContent } from '../cart-dropdown-content/cart-dropdown-content';
import { BottomNavComponent } from '../bottom-nav/bottom-nav';

@Component({
  selector: 'bcm-app-shell',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    NttAvatar,
    NttButton,
    NttDropdownMenu,
    NttDropdownMenuDivider,
    NttDropdownMenuItem,
    NttDropdownMenuItems,
    NttNavButton,
    NttNavHamburger,
    NttNavItem,
    NttNavItemIcon,
    NttSvg,
    Spinner,
    NttDropdownMenuTrigger,
    NttBadge,
    BottomNavComponent,
    CartDropdownContent,
  ],
  templateUrl: './app-shell.component.html',
  styleUrl: './app-shell.component.scss',
})
export class AppShellComponent {
  private readonly _navigationService = inject(NavigationService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _authService = inject(AuthenticationService);
  private readonly _router = inject(Router);
  protected readonly themeService = inject(ThemeService);
  protected readonly cartStore = inject(CartStore);
  protected readonly themeToggleEnabled = inject(THEME_TOGGLE_ENABLED);

  protected readonly navLinks = this._navigationService.navLinks;
  protected readonly accountLinks = this._navigationService.accountLinks;
  protected readonly mobileNavSections =
    this._navigationService.mobileNavSections;
  protected readonly mobileNavOpen = this._navigationService.mobileNavOpen;
  protected readonly logoutLoading = signal(false);

  private readonly _basketMatchOptions: IsActiveMatchOptions = {
    paths: 'subset',
    queryParams: 'ignored',
    fragment: 'ignored',
    matrixParams: 'ignored',
  };

  protected readonly isBasketRoute = isActive(
    '/basket',
    this._router,
    this._basketMatchOptions,
  );

  private readonly _panelMatchOptions: IsActiveMatchOptions = {
    paths: 'subset',
    queryParams: 'ignored',
    fragment: 'ignored',
    matrixParams: 'ignored',
  };

  protected readonly isPanelRoute = isActive(
    '/panel',
    this._router,
    this._panelMatchOptions,
  );

  private readonly _cartMenu = viewChild<NttDropdownMenu>('cartMenu');
  private _cartTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.cartStore.load();
    this._destroyRef.onDestroy(() => this.cartStore.clear());
    this._closeMobileNavOnBreakpointChange();
  }

  private _closeMobileNavOnBreakpointChange(): void {
    const desktop = window.matchMedia('(min-width: 1024px)');
    const close = () => this._navigationService.closeMobileNav();

    desktop.addEventListener('change', close);
    this._destroyRef.onDestroy(() =>
      desktop.removeEventListener('change', close),
    );
  }

  protected handleHamburgerClick(): void {
    this._navigationService.toggleMobileNav();
  }

  protected closeMobileNav(): void {
    this._navigationService.closeMobileNav();
  }

  protected navigateTo(path: string): void {
    void this._router.navigateByUrl(path);
  }

  protected toggleTheme(): void {
    this.themeService.toggle();
  }

  protected async handleLogout(): Promise<void> {
    this.logoutLoading.set(true);
    try {
      await firstValueFrom(this._authService.logout());
      this.cartStore.clear();
      this._navigationService.closeMobileNav();
    } finally {
      this.logoutLoading.set(false);
    }
  }

  protected openCart(): void {
    if (this.isBasketRoute()) return;
    if (this._cartTimeout) {
      clearTimeout(this._cartTimeout);
      this._cartTimeout = null;
    }
    this._cartMenu()?.open();
  }

  protected closeCart(): void {
    if (this.isBasketRoute()) return;
    this._cartTimeout = setTimeout(() => this._cartMenu()?.close(), 200);
  }
}
